import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

export interface MCPServer {
  id: string;
  name: string;
  url: string;
  oauth_enabled: boolean;
  enabled: boolean;
  allowed_operations: string[];
  endpoint_token: string;
  connection_status: 'connected' | 'disconnected' | 'error';
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
  assigned_agents?: number;
}

interface MCPServerCardProps {
  server: MCPServer;
  onEdit: (server: MCPServer) => void;
  onDelete: (serverId: string) => void;
  onCopyLink: (serverId: string) => void;
  onToggleOAuth: (serverId: string, enabled: boolean) => void;
}

/**
 * Card displaying MCP server information with actions
 * - Shows connection status with color-coded indicator
 * - OAuth toggle
 * - Copy MCP link button
 * - Edit/Delete actions
 * - Mobile-friendly layout
 */
export function MCPServerCard({
  server,
  onEdit,
  onDelete,
  onCopyLink,
  onToggleOAuth,
}: MCPServerCardProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      await onDelete(server.id);
    } finally {
      setIsDeleting(false);
      setShowConfirmDelete(false);
    }
  }, [onDelete, server.id]);

  const statusColors = {
    connected: 'bg-green-500',
    disconnected: 'bg-slate-500',
    error: 'bg-red-500',
  };

  const statusText = {
    connected: 'Connected',
    disconnected: 'Disconnected',
    error: 'Error',
  };

  return (
    <div
      className="bg-slate-800 border border-slate-700 rounded-lg p-4 sm:p-6 space-y-4"
      data-testid={`mcp-server-${server.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-white truncate">{server.name}</h3>
            <div
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                server.connection_status === 'connected'
                  ? 'bg-green-500/20 text-green-400'
                  : server.connection_status === 'error'
                    ? 'bg-red-500/20 text-red-400'
                    : 'bg-slate-600/20 text-slate-400'
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${statusColors[server.connection_status]}`} />
              {statusText[server.connection_status]}
            </div>
          </div>
          <p className="text-sm text-slate-400 truncate">{server.url}</p>
          {server.last_used_at && (
            <p className="text-xs text-slate-500 mt-1">
              Last connected: {new Date(server.last_used_at).toLocaleString()}
            </p>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <label className="text-slate-400 block mb-1">OAuth</label>
          <button
            onClick={() => onToggleOAuth(server.id, !server.oauth_enabled)}
            className={`min-w-[44px] min-h-[44px] sm:min-h-[36px] px-3 py-2 rounded-lg font-medium transition-colors touch-manipulation ${
              server.oauth_enabled
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
            data-testid={`oauth-toggle-${server.id}`}
          >
            {server.oauth_enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Assigned Agents</label>
          <div className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white">
            {server.assigned_agents || 0}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => onCopyLink(server.id)}
          variant="secondary"
          size="sm"
          className="flex-1 sm:flex-initial min-h-[44px] touch-manipulation"
          data-testid={`copy-link-${server.id}`}
        >
          Copy MCP Link
        </Button>
        <Button
          onClick={() => onEdit(server)}
          variant="secondary"
          size="sm"
          className="flex-1 sm:flex-initial min-h-[44px] touch-manipulation"
          data-testid={`edit-server-${server.id}`}
        >
          Edit
        </Button>
        <Button
          onClick={() => setShowConfirmDelete(true)}
          variant="danger"
          size="sm"
          disabled={isDeleting}
          className="flex-1 sm:flex-initial min-h-[44px] touch-manipulation"
          data-testid={`delete-server-${server.id}`}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </Button>
      </div>

      <ConfirmDialog
        isOpen={showConfirmDelete}
        title="Delete Server"
        message={`Are you sure you want to delete "${server.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setShowConfirmDelete(false)}
      />
    </div>
  );
}
